import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, Menu, Moon, Sun, X } from "lucide-react";
import { LinkButton } from "@/components/ui/Button";
import { useSession, isPatient, isAdmin } from "@/lib/session";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/cn";

/**
 * Chrome for the pages a visitor can reach without an account.
 *
 * The public routes previously rendered bare, with no way to reach login,
 * registration, or the other services — the chatbot was a dead end.
 */

const NAV = [
  { to: "/", label: "Beranda", end: true },
  { to: "/chat", label: "Tanya Mediora" },
  { to: "/triage", label: "Triase Gejala" },
  { to: "/reservasi", label: "Reservasi" },
];

export function PublicShell() {
  const [open, setOpen] = useState(false);
  const { data: session } = useSession();
  const { theme, toggle } = useTheme();

  const signedIn = isPatient(session) || isAdmin(session);
  const portalPath = isAdmin(session) ? "/admin" : "/patient/dashboard";
  // An admin reading "Portal saya" has no reason to expect the admin panel —
  // it names the patient portal, which is the one place the button does not go.
  const portalLabel = isAdmin(session) ? "Panel admin" : "Portal saya";
  const portalLabelShort = isAdmin(session) ? "Panel" : "Portal";

  return (
    // h-dvh + min-h-0 so a full-height child (the chat thread) can own its own
    // scrolling instead of growing the page.
    <div className="flex h-dvh flex-col">
      <header className="border-hairline bg-canvas/85 sticky top-0 z-30 border-b backdrop-blur-md">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-3 px-4 sm:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span
              aria-hidden
              className="bg-accent text-accent-content font-display grid size-8 place-items-center text-base font-extrabold"
              style={{ borderRadius: "0.7rem 0.2rem 0.7rem 0.2rem" }}
            >
              M
            </span>
            <span className="font-display leading-none font-extrabold tracking-tight">
              mediora
            </span>
          </Link>

          <nav aria-label="Navigasi layanan" className="ml-4 hidden items-center gap-1 md:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "rounded-(--radius-control) px-3 py-1.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-surface-sunken text-content font-semibold"
                      : "text-content-muted hover:text-content",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex-1" />

          <button
            type="button"
            onClick={toggle}
            aria-label={theme === "dark" ? "Gunakan tema terang" : "Gunakan tema gelap"}
            className="hover:bg-surface-sunken text-content-muted hover:text-content grid size-9 shrink-0 place-items-center rounded-lg"
          >
            {theme === "dark" ? <Sun className="size-[18px]" /> : <Moon className="size-[18px]" />}
          </button>

          {signedIn ? (
            <LinkButton
              to={portalPath}
              size="sm"
              variant="primary"
              icon={<LayoutDashboard className="size-4" />}
            >
              <span className="hidden sm:inline">{portalLabel}</span>
              <span className="sm:hidden">{portalLabelShort}</span>
            </LinkButton>
          ) : (
            <div className="hidden items-center gap-2 sm:flex">
              <LinkButton to="/patient/login" size="sm" variant="ghost">
                Masuk
              </LinkButton>
              <LinkButton to="/patient/register" size="sm" variant="primary">
                Daftar
              </LinkButton>
            </div>
          )}

          <button
            type="button"
            onClick={() => setOpen((current) => !current)}
            aria-label={open ? "Tutup menu" : "Buka menu"}
            aria-expanded={open}
            className="hover:bg-surface-sunken -mr-1 grid size-9 shrink-0 place-items-center rounded-lg md:hidden"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>

        {open && (
          <div className="border-hairline bg-canvas border-t px-4 py-3 md:hidden">
            <nav aria-label="Navigasi layanan" className="grid gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "rounded-xl px-3 py-2 text-sm font-medium",
                      isActive
                        ? "bg-surface-sunken text-content font-semibold"
                        : "text-content-muted",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            {!signedIn && (
              <div className="border-hairline mt-3 grid grid-cols-2 gap-2 border-t pt-3">
                <LinkButton to="/patient/login" fullWidth onClick={() => setOpen(false)}>
                  Masuk
                </LinkButton>
                <LinkButton
                  to="/patient/register"
                  variant="primary"
                  fullWidth
                  onClick={() => setOpen(false)}
                >
                  Daftar
                </LinkButton>
              </div>
            )}
          </div>
        )}
      </header>

      {/* The landmark lives here rather than in each screen: triage and
          reservation also render inside the patient shell, which supplies its
          own <main>, and two nested mains is invalid. */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
